<template>
  <MysteryButton
    v-if="moduleData.options.button_style === 'mystery-science' && draftIsMysteryTheme"
    class="advanced-button-module advanced-button-module--mystery"
    :color="moduleData.options.theme && moduleData.options.theme.variation === 2
      ? 'purple'
      : 'black'"
    :link="buttonAction === 'link' && mode === 'VIEW' && target || undefined"
    :target="buttonAction === 'link' && mode === 'VIEW' && '_blank'"
    type="box"
    @click="navigate"
  >
    <StudioTextEditor
      ref="textEditor"
      :id="id"
      :canvas-name="canvasName"
      :mode="mode"
      :module-active="active"
      :content="moduleData.options.content"
      :data-type="moduleData.options.data_type"
      :text-placeholder="textPlaceholder"
      :scale="scale"
      floating-menu
      :disabled="isLocked(id)"
      :watch-content="textEditorShouldWatchForContentUpdates"
      :get-before-module-container-ref="getBeforeModuleContainerRef"
      :show-empty-styles="false"
      parent-module-can-rotate
      disable-link
      class="advanced-button-module__text-editor"
      @update="updateContent"
    />
  </MysteryButton>
  <component
    v-else
    class="advanced-button-module"
    :class="[
      {
        'component-module-wrapper__module-contents--shape-mask': moduleData
          && moduleData.options && moduleData.options.shape
          && moduleData.options.shape !== 'default',
      },
      themeClass,
    ]"
    :style="style"
    :is="componentTag"
    :href="buttonAction === 'link' && mode === 'VIEW' && target ? target : undefined"
    :target="buttonAction === 'link' && mode === 'VIEW' ? '_blank' : undefined"
    @click="navigate"
  >
    <StudioTextEditor
      ref="textEditor"
      :id="id"
      :canvas-name="canvasName"
      :mode="mode"
      :module-active="active"
      :content="moduleData.options.content"
      :data-type="moduleData.options.data_type"
      :text-placeholder="textPlaceholder"
      :scale="scale"
      floating-menu
      :disabled="isLocked(id)"
      :watch-content="textEditorShouldWatchForContentUpdates"
      :get-before-module-container-ref="getBeforeModuleContainerRef"
      :parent-module-autosize-enabled="autoSizeEnabled(moduleData)"
      :show-empty-styles="false"
      parent-module-can-rotate
      disable-link
      disable-text-color
      class="advanced-button-module__text-editor"
      @update="updateContent"
    />
  </component>
</template>

<script>
import { MysteryButton } from '@discoveryedu/mystery-components';
import { cloneDeep, get } from 'lodash-es';
import { mapActions, mapState } from 'pinia';
import { updateDomainForDELinks } from '@discoveryedu/titan-text-editor';
import * as types from '@/lib/constants/store';
import { getTemplatePlaceholders } from '@/lib/constants';
import { encodeHTML } from '@/lib/utils';
import { formatUrl } from '@/lib/utils/url';
import navigation from '@/mixins/navigation';
import autoSizeModule from '@/mixins/autoSizeModule';
import StudioTextEditor from '@/components/StudioTextEditor.vue';
import { themes } from '@/lib/constants/themes.json';
import {
  useEditorStore,
} from '@/stores';

export default {
  name: 'AdvancedButtonModule',
  components: {
    MysteryButton,
    StudioTextEditor,
  },
  mixins: [navigation, autoSizeModule],
  props: {
    active: Boolean,
    id: {
      type: String,
      required: true,
    },
    mode: {
      type: String,
      required: true,
    },
    moduleData: {
      type: Object,
      required: true,
    },
    canvasName: {
      type: String,
      required: true,
    },
    scale: {
      type: Number,
      default: 1,
    },
    getBeforeModuleContainerRef: {
      type: Function,
      default: null,
    },
    page: {
      type: Object,
      default: null,
    },
  },
  computed: {
    ...mapState(useEditorStore, [
      'domains',
      'isLocked',
      'currentlyEditing',
      'currentPage',
    ]),
    buttonAction() {
      return this.moduleData?.options?.action;
    },
    componentTag() {
      // In edit mode, the button shouldn't do anything since the user is editing text
      if (this.mode === 'EDIT') return 'div';

      // Otherwise it should be an interactive <button> or <a>
      if (this.buttonAction === 'page' || this.buttonAction === 'window') return 'button';

      return 'a';
    },
    draftIsMysteryTheme() {
      return get(this, 'currentPage.options.theme.name') === 'mystery-science';
    },
    style() {
      // New variation-based themes override the old style-based themes
      if (this.themeClass) return null;

      // Note: This purposely leaves out the background color, which is handled in css
      // so it can receive a hover state
      return this.moduleData.options.style;
    },
    textPlaceholder() {
      // Template slides may contain placeholder text that needs to be localized,
      // which is wrapped with a $t{} syntax
      const placeholder = this.moduleData.options.text_placeholder;
      const placeholderKey = placeholder && placeholder.match(/\$t{(.*)}/);
      const templatePlaceholders = getTemplatePlaceholders();

      if (placeholderKey && placeholderKey[1] && templatePlaceholders[placeholderKey[1]]) {
        return placeholder.replace(/\$t{(.*)}/, templatePlaceholders[placeholderKey[1]]);
      }

      return placeholder || this.$t('Button');
    },
    theme() {
      const themeId = (this.page || this.currentPage)?.options?.theme?.name;
      return themes.find((theme) => theme.id === themeId);
    },
    themeClass() {
      let variation = get(this, 'moduleData.options.theme.variation');
      if (!variation) return undefined;
      if (this.theme) {
        const hasButtonColor = !!(this.theme.buttonColors || []).find((c) => c.id === variation);
        if (!hasButtonColor) variation = 1;
      }
      return `editor-theme__button--${variation}`;
    },
    textEditorShouldWatchForContentUpdates() {
      /*
        Text editor shouldn't watch for content updates if the current user is editing,
        otherwise cursor position won't be maintained properly.
      */
      return !get(this.currentlyEditing(this.id), 'isCurrentUser');
    },
    target() {
      const target = formatUrl(get(this, 'moduleData.options.target', ''));

      if (this.mode === 'VIEW' && target) {
        return updateDomainForDELinks(target, this.domains.appDomain);
      }

      return target;
    },
  },
  methods: {
    ...mapActions(useEditorStore, [
      types.UPDATE_MODULE,
      types.VIEW_MODAL_CANVAS_BY_ID,
      types.VIEW_PREV_PAGE,
      types.VIEW_NEXT_PAGE,
      types.VIEW_PAGE_BY_ID,
    ]),
    updateContent({ json }) {
      const data = cloneDeep(this.moduleData);
      data.options.content = encodeHTML(json);
      if (data.options.data_type) {
        delete data.options.data_type;
      }
      this[types.UPDATE_MODULE]({ canvasName: this.canvasName, payload: data });
    },
    navigate(e) {
      // In edit mode, we should never navigate on click
      if (this.mode === 'EDIT') return;

      this.handleViewModeAnchorClick(e);

      if (this.buttonAction === 'page') {
        // Otherwise navigate if it's a button
        const targetPage = this.moduleData.options.target_page;

        if (targetPage === 'PREV') {
          this[types.VIEW_PREV_PAGE]();
        } else if (targetPage === 'NEXT') {
          this[types.VIEW_NEXT_PAGE]();
        } else {
          this[types.VIEW_PAGE_BY_ID](targetPage);
        }
      } else if (this.buttonAction === 'window') {
        this[types.VIEW_MODAL_CANVAS_BY_ID](this.moduleData.options.target_page);
      }
    },
  },
};
</script>

<style lang="stylus">
.advanced-button-module {
  break-inside: avoid; // Avoid splitting across pages when printing
  display: block;
  height: 100%;
  padding: 0;
  text-align: start;
  width: 100%;

  &:not(.advanced-button-module--mystery) {
    // Mystery buttons have their own borders that we shouldn't override
    border: none;
  }

  &--mystery {
    // Leave room for the button shadow within the bounds of the module
    height: calc(100% - 6px);
    width: calc(100% - 6px);
  }

  &__text-editor {
    border-radius: 6px; // match radius for other modules
    height: 100%;
    position: absolute;
    top: 0;
    width: 100%;
  }
}
</style>
